// Libraries
import React from 'react'

// Constants
import { ModuleNames } from '../../../constants/componentNames.constants'
import { LocaleCodes } from '../../../constants/localization.constants'

// Components
import ContentBlock from '../../global/contentBlock'
import Hero from '../../global/hero'
import MainCTA from '../../global/mainCta'
import ResourceRow from '../../global/resourceRow'
import SEOTags from '../../global/seoTags'
import VideoSpotlight from '../../global/videoSpotlight'
import Layout from '../../layouts/layout'
import { ResourceDemoTagsContainer } from './style'

import Typography from '../../global/typography'

// helpers
import { findTarget } from '../../../utils/microcopy.utils'

const ResourceDemoPage = ({
  pageContext,
  location,
  path,
  headerData,
  footerData,
  announcementData,
  mainNavBannerData,
  browserLanguageData,
}: any) => {
  let dynamicPageContent: any
  let hero: any
  let spotlight: any
  let ctaSection: any
  let pageData = pageContext.resourceDemoData ? pageContext.resourceDemoData : ''

  let relatedDemos = [],
    generics: { key: string; value: any }[] = [],
    dynamicRow: any,
    dynamicContent: any
  if (pageContext.relatedResourceDemos) {
    relatedDemos = pageContext.relatedResourceDemos
  }

  generics = pageData?.microcopy?.values || []
  if (pageContext.relatedResourceDemo) {
    relatedDemos = pageContext.relatedResourceDemo.map((ele: any) => {
      return {
        hero: ele.hero || {},
        teaserBody: ele.teaserBody || '',
        seoMetaData: ele.seoMetadata || {},
        category: ele.resourceCategory || { name: 'Video Demo', number: '6' },
        microcopy: ele.microcopy || {},
        spotlight: ele.spotlight || {},
        slug: ele.slug || '',
        __typename: ele.__typename,
      }
    })
    dynamicRow = {
      __typename: 'ContentfulComponentResourceRow',
      topPadding: 'Small',
      bottomPadding: 'Small',
      featuredPages: relatedDemos,
    }
    dynamicContent = {
      bottomPadding: 'None',
      contentBlockTheme: 'Grey',
      headline: findTarget(`.relatedVideos.text`, generics, false) || 'Related video demos',
      orangeDivider: false,
      textAlignment: true,
      topPadding: 'Small',
    }
  }

  let defaultLocale: string
  if (pageContext && pageContext.pageLocale) {
    defaultLocale = pageContext.pageLocale
  } else {
    defaultLocale = LocaleCodes.ENGLISH.code
  }

  if (pageData && pageData.hero) {
    let pageTitle = ''
    if (pageData.pageName) {
      pageTitle = pageData.pageName
    } else if (pageData.name) {
      pageTitle = pageData.name
    }

    hero = (
      <Hero
        data={pageData.hero}
        location={location}
        pageTitle={pageTitle}
        showBreadcrumbs={true}
        locale={defaultLocale}
        breadcrumb={pageData.breadcrumb}
      />
    )
  }

  if (pageData && pageData.spotlight) {
    spotlight = <VideoSpotlight data={pageData.spotlight} useCase="Resource"></VideoSpotlight>
  }

  if (pageData && pageData.bottomCta) {
    ctaSection = <MainCTA data={pageData.bottomCta}></MainCTA>
  }

  if (pageData && pageData.flexSection) {
    dynamicPageContent = pageData.flexSection.map((module: any, i: Number) => {
      switch (module.__typename) {
        case ModuleNames.GLOBAL_CONTENT_BLOCK:
          return (
            <ContentBlock
              data={module}
              useCase="Resource"
              key={`page-component--${i}`}></ContentBlock>
          )
        case ModuleNames.ANCHOR_LINK:
          const identifier = module?.identifier ? module.identifier : ''
          return <span id={identifier}></span>

        case ModuleNames.RESOURCE_ROW:
          return (
            <ResourceRow
              data={module}
              key={`page-component--${i}`}
              useCase="Resource"
              locale={defaultLocale}></ResourceRow>
          )
      }
    })
  } else if (relatedDemos && relatedDemos.length > 0) {
    dynamicPageContent = [
      <ContentBlock data={dynamicContent} useCase="Resource"></ContentBlock>,
      <ResourceRow data={dynamicRow} useCase="Resource" locale={defaultLocale}></ResourceRow>,
    ]
  }

  const defaultSlug = pageData && pageData.slug ? pageData.slug : '/'
  const seoData = pageData && pageData.seoMetadata ? pageData.seoMetadata : null

  const formattedDemoTopics =
    pageData && pageData.topics && pageData.topics.length && pageData.topics.length > 0
      ? pageData.topics.map((topic: any) => {
          topic.url = topic.title
            ? `/resources/${topic.title
                .toLowerCase()
                .replace(/ *\([^)]*\) */g, '')
                .replace(/\s/g, '-')}`
            : 'https://www.uipath.com/resources/all'

          return topic
        })
      : []

  const renderTopics = () => {
    if (formattedDemoTopics.length > 0) {
      return (
        <ResourceDemoTagsContainer>
          <div className="Tags__Group">
            <Typography type="label" className="Tags__Heading">
              {findTarget(`resource.6.TopicsLABEL.text`, generics) || 'Topics:'}
            </Typography>
            {formattedDemoTopics &&
              formattedDemoTopics.map((tag: any) => {
                if (tag.title) {
                  return (
                    <a
                      key={tag.title}
                      className="Tag CustomerStory__Tag"
                      href="https://www.uipath.com/resources/all">
                      {tag.title}
                    </a>
                  )
                }
              })}
          </div>
        </ResourceDemoTagsContainer>
      )
    }
  }

  const pageInner = (
    <div data-cy="PageResourceDemoPage">
      <SEOTags
        seoMetadata={seoData}
        path={path}
        slug={defaultSlug}
        locale={defaultLocale}
        siteSection="Resource Demo"
        availableLocales={pageContext.availableLocales}
      />
      {hero}
      {spotlight}
      {dynamicPageContent}
      {renderTopics()}
      {ctaSection}
    </div>
  )

  return (
    <Layout.Template
      lang={defaultLocale}
      headerData={headerData}
      footerData={footerData}
      announcementData={announcementData}
      mainNavBannerData={mainNavBannerData}
      browserLanguageData={browserLanguageData}
      pageType="resourceDemo"
      slug={defaultSlug}
      availableLocales={pageContext.availableLocales}>
      {pageInner}
    </Layout.Template>
  )
}

export default ResourceDemoPage
